import React from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";

interface PasswordResetFormProps {
  email: string;
  setEmail: (email: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBackToSignIn: () => void;
  loading: boolean;
  resetSent: boolean;
  resetError: string | null;
}

export const PasswordResetForm = ({
  email,
  setEmail,
  onSubmit,
  onBackToSignIn,
  loading,
  resetSent,
  resetError
}: PasswordResetFormProps) => {
  return (
    <Card className="border-0 shadow-xl">
      <CardHeader className="space-y-1">
        <div className="mb-2">
          <img 
            src="https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/15521506-bf83-4c94-881f-2ed325767800/public" 
            alt="Hivetivity Logo"
            className="h-10 mx-auto" 
          />
        </div>
        <CardTitle className="text-2xl font-bold text-center">Reset Password</CardTitle>
        <CardDescription className="text-center">
          Enter your email to receive a password reset link
        </CardDescription>
      </CardHeader>
      <CardContent>
        {resetSent ? (
          <motion.div 
            className="text-center py-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-green-50 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="text-xl font-medium mb-2">Check your inbox</h3>
            <p className="text-gray-500 mb-6">
              We've sent a password reset link to <span className="font-medium">{email}</span>
            </p>
            <Button
              variant="outline"
              onClick={onBackToSignIn}
              className="mt-2"
            >
              Back to sign in
            </Button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email-reset" className="font-medium">Email</Label>
                <Input 
                  id="email-reset" 
                  type="email" 
                  placeholder="email@example.com" 
                  value={email} 
                  onChange={e => setEmail(e.target.value)} 
                  className="px-3"
                  required 
                />
              </div>
              
              {resetError && (
                <div className="bg-destructive/10 text-destructive px-3 py-2 rounded-md text-sm">
                  {resetError}
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="animate-spin mr-2 h-4 w-4 border-2 border-t-transparent border-white rounded-full"></span>
                    Sending link...
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={onBackToSignIn}
                className="w-full flex items-center justify-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to sign in
              </Button>
            </form>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
};
